import { Injectable } from '@angular/core';
import { RequiredDocument } from '@shared/entities/requiredDocument';
import { ApiService } from '@shared/services';
import { Observable, catchError, map, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Attachment } from '@shared/entities/attachment';
import { environment } from '@environments/environment';

const LOG_PREFIX = '[Api][RequiredDocumentForm] ';

@Injectable({
  providedIn: 'root'
})
export class RequiredDocumentFormService extends ApiService<RequiredDocument> {
  constructor(private http: HttpClient) {
    const masterUrl = '/required-documents';
    const detailUrl = '/required-documents';
    super(RequiredDocument, masterUrl, detailUrl, undefined, 'required-document-form');
  }

  findRequiredDocument(id: string): Observable<RequiredDocument | null> {
    return this.findOne(id).pipe(
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'findRequiredDocument error', error);
        return of(null);
      })
    );
  }

  saveRequiredDocument(documentData: RequiredDocument): Observable<RequiredDocument | null> {
    this.removeUrlParameters();

    const requiredDocument = Object.assign(new RequiredDocument(), documentData);

    return this.save(requiredDocument).pipe(
      map((response) => {
        return response || requiredDocument;
      }),
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'saveRequiredDocument error', error);
        return of(error);
      })
    );
  }

  editRequiredDocument(documentData: RequiredDocument, documentId: string): Observable<RequiredDocument | null> {
    this.removeUrlParameters();

    return this.save(documentData, documentId).pipe(
      map((response) => {
        return response || documentData;
      }),
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'editRequiredDocument error', error);
        return of(error);
      })
    );
  }

  findByFilter(id: string): Observable<RequiredDocument | null> {
    this.setUrlParameter('id', id);

    return this.paginate(RequiredDocument, 1, 1).pipe(
      map((paginate) => {
        if (paginate && paginate.data && paginate.data.length > 0) {
          return paginate.data[0];
        }
        return null;
      }),
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'findByFilter error', error);
        return of(null);
      })
    );
  }

  deleteRequiredDocument(id: string): Observable<boolean | any> {
    this.removeUrlParameters();
    const url = `${environment.apiBaseUrl}/required-documents?id=${id}`;
    return this.http.delete(url).pipe(
      map(() => {
        return true;
      }),
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'deleteRequiredDocument error', error);
        return of({ isError: true, error: error });
      })
    );
  }

  uploadAttachment(file: File | FormData): Observable<Attachment> {
    this.removeUrlParameters();

    const formData = this.createFormData(file);

    return this.postMultipartData<Attachment>('/attachments', formData, Attachment).pipe(
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'uploadAttachment error', error);
        throw error;
      })
    );
  }

  deleteFile(fileId: string): Observable<boolean | null> {
    return this.http.delete<boolean>(`/attachments/${fileId}`).pipe(
      map(() => {
        return true;
      }),
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'deleteFile error', error);
        return of(null);
      })
    );
  }

  saveRequiredDocumentWithAttachments(
    documentData: RequiredDocument,
    files: Record<string, File>
  ): Observable<RequiredDocument> {
    this.removeUrlParameters();

    const formData = new FormData();
    formData.append('request', JSON.stringify(documentData));
    Object.entries(files).forEach(([key, file]) => {
      formData.append(key, file);
    });

    return this.postMultipartData<RequiredDocument>('/required-documents', formData, RequiredDocument).pipe(
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'saveRequiredDocumentWithAttachments error', error);
        throw error;
      })
    );
  }

  updateRequiredDocumentWithAttachments(
    documentId: string,
    documentData: RequiredDocument,
    files: Record<string, File>
  ): Observable<RequiredDocument> {
    this.removeUrlParameters();

    const formData = new FormData();
    documentData.id = documentId;
    formData.append('request', JSON.stringify(documentData));
    Object.entries(files).forEach(([key, file]) => {
      formData.append(key, file);
    });

    return this.putMultipartData<RequiredDocument>('/required-documents', formData, RequiredDocument).pipe(
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'updateRequiredDocumentWithAttachments error', error);
        throw error;
      })
    );
  }

  private createFormData(fileOrFormData: File | FormData): FormData {
    if (fileOrFormData instanceof FormData) {
      return fileOrFormData;
    }

    const formData = new FormData();
    formData.append('file', fileOrFormData, fileOrFormData.name);
    return formData;
  }

  uploadMultipleAttachments(files: File[]): Observable<Attachment[]> {
    this.removeUrlParameters();

    if (files.length === 0) {
      return of([]);
    }

    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file, file.name);
    });

    return this.postMultipartDataArray<Attachment>('/attachments/batch', formData, Attachment).pipe(
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'uploadMultipleAttachments error', error);
        throw error;
      })
    );
  }
}
